import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What is a treasury-vehicle preferred stock?',
    a: 'A perpetual preferred share issued specifically to raise capital that the sponsor deploys into a treasury asset (usually Bitcoin) instead of a traditional operating business. SATA (Strive), STRK and STRD (Strategy) are the flagship examples. The dividend is paid on a stated par value (typically $100), funded by capital raises and treasury reserves rather than operating earnings, and the rate is variable — re-set monthly by the board.',
  },
  {
    q: 'Why do EPS, ROE, ROIC and payout ratio look broken for these stocks?',
    a: 'Because those formulas assume an operating company. GAAP forces the sponsor to run volatile treasury assets through the income statement, creating huge paper losses that wreck net income. That drives ROE and ROIC deeply negative and makes the payout ratio compute as 0% or nonsense. None of it describes the health of the preferred itself. DiviDrip detects these vehicles automatically and swaps in a structure-aware checklist instead.',
  },
  {
    q: 'What actually matters when judging a treasury-vehicle preferred?',
    a: 'Four things: (1) price anchoring — is the share trading within a few percent of its stated par value, (2) the declaration record — is the board declaring and paying the variable rate on schedule, (3) the capital stack — zero debt above the preferred means you hold the senior claim on all residual assets, and (4) sponsor reserves — how long the sponsor can fund distributions without selling its treasury assets into a downturn.',
  },
  {
    q: 'Why are the dividends classified as Return of Capital (ROC)?',
    a: 'Because they are not paid out of corporate earnings and profits. In a taxable account an ROC distribution is not taxed when received — instead it lowers your cost basis by the same amount. When you eventually sell, the lowered basis produces a larger capital gain. Once your basis reaches $0, further distributions become immediately taxable capital gains. Inside a Roth or Traditional IRA none of this basis tracking applies.',
  },
  {
    q: 'Is the yield safe just because the checklist passes?',
    a: 'No. The rate is variable by design — the board can cut it any month to defend the par anchor, and the long-run sustainability depends on the sponsor\u2019s treasury (often Bitcoin) holding its value and on continued investor demand for new share issuance. A passing checklist means the structure is currently behaving as designed, not that the yield is guaranteed.',
  },
];

export default function LearnTreasuryVehiclePreferreds() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="Treasury-Vehicle Preferreds — Why SATA, STRK & STRD Break Normal Stock Math | DiviDrip"
      description="SATA, STRK and STRD are equity-funded treasury vehicles, not operating companies. Here is why EPS, ROE and payout ratio are meaningless for them, what to judge instead, and how Return of Capital changes your taxes."
      canonical="https://twylightcrow.com/learn/treasury-vehicle-preferreds"
      guidePath="/learn/treasury-vehicle-preferreds"
      sectionLabel="Dividend Basics"
      sectionAnchor="basics"
      kicker="Learn · Specialized structures"
      h1="Treasury-Vehicle Preferreds &mdash; When Normal Stock Math Stops Working"
      datePublished="2026-06-29"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/daily-dividend-stocks', label: 'Daily Dividend Stocks — SATA, the First-Ever Daily Payer' },
        { to: '/learn/payout-ratio', label: 'Payout Ratio — the classic safety gauge (for operating companies)' },
      ]}
    >
      <h2 id="what-it-is">A fund wearing a corporate wrapper</h2>
      <p>
        Some tickers on your screen are not businesses at all. SATA (Strive), STRK and STRD
        (Strategy) are <strong>perpetual preferred shares issued to raise capital that the sponsor
        immediately deploys into a treasury asset</strong> — in every current case, Bitcoin. There is
        no factory, no product margin, no earnings runway behind the dividend. The share is a
        financing instrument: you hand the sponsor $100 of capital, and the board declares a
        variable monthly rate on that $100 stated par value.
      </p>
      <p>
        When you see one of these on DiviDrip, it carries an indigo{' '}
        <strong>Treasury Vehicle</strong> badge in the stock modal header. That badge means the
        Triangle checklist, DiviDrip&rsquo;s Opinion, and the payout-ratio display have all switched
        into structure-aware mode for that ticker.
      </p>

      <h2 id="broken-math">Why the normal metrics read as garbage</h2>
      <p>
        Standard screeners are hardcoded for operating companies. Point them at a treasury vehicle
        and every headline metric corrupts:
      </p>
      <ul>
        <li>
          <strong>EPS growth</strong> — the preferred has no earnings of its own, and the
          sponsor&rsquo;s common-stock earnings are dominated by mark-to-market swings on the
          treasury. A &ldquo;-11% CAGR&rdquo; tells you nothing about your distribution.
        </li>
        <li>
          <strong>ROE and ROIC</strong> — GAAP revalues volatile treasury assets through the income
          statement, so paper losses obliterate net income and the ratios plunge to numbers like
          -150%. That is accounting friction, not capital destruction of the preferred.
        </li>
        <li>
          <strong>Payout ratio</strong> — dividends divided by earnings breaks completely when the
          dividend is not paid from earnings. A screener showing 0% and calling it &ldquo;very
          safe&rdquo; has it exactly backwards — the honest answer is{' '}
          <strong>N/A, funded by capital raises</strong>, which is what DiviDrip now shows.
        </li>
      </ul>

      <h2 id="what-to-judge">The four checks that actually matter</h2>
      <p>
        For flagged tickers the Triangle tab grades the structure on its own terms:
      </p>
      <ul>
        <li>
          <strong>Price anchored to par</strong> — the whole design promises a share that hugs its
          stated value (usually $100). Trading within ±5% of par means the anchor mechanism is
          holding, and a wide break means the market is repricing the risk.
        </li>
        <li>
          <strong>Distribution declared</strong> — the rate is variable and re-set monthly by the
          board, so an unbroken declaration record is the closest thing to a streak.
        </li>
        <li>
          <strong>Sponsor leverage</strong> — with zero debt above the preferred, holders sit at the
          very top of the liquidation waterfall and claim the residual assets first. Senior debt
          appearing on the balance sheet weakens that claim.
        </li>
        <li>
          <strong>Yield sanity</strong> — a rate far above what peers sustain is a signal the board
          may reset it downward to protect the par anchor.
        </li>
      </ul>
      <p>
        One thing no checklist can score is <strong>reserve runway</strong> — how long the sponsor
        can keep paying without selling treasury assets into a crash. Strive, for example, has
        described holding roughly 18 months of distribution coverage in cash-like reserves. Read the
        sponsor&rsquo;s filings for the current figure before sizing a position.
      </p>

      <h2 id="roc-taxes">Return of Capital — the tax twist</h2>
      <p>
        Because the distributions are not paid from earnings and profits, they are classified as{' '}
        <strong>Return of Capital</strong>. In a taxable account nothing is taxed when the cash
        lands — instead each payout <strong>lowers your cost basis</strong> by the same amount.
        Sell later and the lowered basis produces a bigger capital gain, and if you hold long enough
        for the basis to reach $0, every further payout becomes an immediately taxable capital gain.
        DiviDrip surfaces a reminder on treasury-vehicle rows in your{' '}
        <Link to="/tax-lots">Tax Lots</Link> and year-end views. Inside a Roth or Traditional IRA
        the entire basis-tracking exercise disappears.
      </p>

      <h2 id="detection">How DiviDrip detects them</h2>
      <p>
        Detection is automatic and data-driven: the security must be typed as a preferred share by
        our market-data provider <em>and</em> its official description must talk about treasury
        operations funded by digital assets. Operating-company common stocks that merely hold
        Bitcoin on the side (MicroStrategy-style) get a softer informational treatment and keep
        their normal scoring, and REITs and BDCs — which legitimately pay from mandated
        distributions — are excluded by design. A weekly sweep re-scans the whole universe, so new
        copycat issues classify themselves without a code change.
      </p>

      <h2 id="faq">FAQ</h2>
      {FAQS.map(({ q, a }) => (
        <details key={q} className="learn-faq mt-4 cursor-help">
          <summary className="cursor-help">{q}</summary>
          <p>{a}</p>
        </details>
      ))}
    </LearnPage>
  );
}
